import { useEffect, useState } from "react"
import InfoBox from "../ui/InfoBox"
import SectionHeader from "../ui/SectionHeader"
import { FiGlobe } from "react-icons/fi"
import { euLanguages } from "../../utils/countryData"
import { useResetListener } from "../../hooks/useResetListener"

// EPO official languages (Art. 14(1) EPC): the language of the
// proceedings must be one of these three.
const EPO_LANGUAGES = ["English", "French", "German"]

interface PatentLanguageSectionProps {
  proceedingsLanguage: string
  onProceedingsLanguageChange: (value: string) => void
  applicationLanguage: string
  onApplicationLanguageChange: (value: string) => void
  examinationInNonOfficialLanguage: boolean
  onExaminationInNonOfficialLanguageChange: (value: boolean) => void
  disabled?: boolean
}

export default function PatentLanguageSection({
  proceedingsLanguage,
  onProceedingsLanguageChange,
  applicationLanguage,
  onApplicationLanguageChange,
  examinationInNonOfficialLanguage,
  onExaminationInNonOfficialLanguageChange,
  disabled = false
}: PatentLanguageSectionProps) {
  // The application language only ever needs to be shown as a separate
  // choice when it actually differs from the proceedings language (a
  // filer opting into Art. 14(2) EPC) — in the common case it's kept in
  // sync automatically, so there is nothing extra to fill in.
  const [showDifferentLanguage, setShowDifferentLanguage] = useState(
    Boolean(applicationLanguage) && applicationLanguage !== proceedingsLanguage
  )

  useEffect(() => {
    if (!showDifferentLanguage) {
      onApplicationLanguageChange(proceedingsLanguage)
    }
    // Only re-sync when the proceedings language or the toggle changes —
    // not on every onApplicationLanguageChange identity change.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [proceedingsLanguage, showDifferentLanguage])

  useResetListener(() => {
    setShowDifferentLanguage(false)
    onProceedingsLanguageChange("")
    onApplicationLanguageChange("")
    onExaminationInNonOfficialLanguageChange(false)
  })

  return (
    <div>
      <SectionHeader icon={<FiGlobe size={20} />} title="Language">
        <InfoBox title="Language of the proceedings (Art. 14(1) EPC)">
          Choose the language of the proceedings before the EPO. It must
          be English, French or German.
        </InfoBox>
      </SectionHeader>

      <div className="flex flex-wrap gap-3">
        {EPO_LANGUAGES.map((language) => (
          <label
            key={language}
            className={`flex cursor-pointer items-center gap-2 rounded-xl border px-4 py-2.5 text-sm font-medium transition ${
              proceedingsLanguage === language
                ? "border-dantzBlue bg-blue-50 text-dantzBlue"
                : "border-gray-200 bg-gray-50 text-gray-700 hover:border-gray-300"
            } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
          >
            <input
              type="radio"
              name="patent-proceedings-language"
              className="sr-only"
              checked={proceedingsLanguage === language}
              disabled={disabled}
              onChange={() => !disabled && onProceedingsLanguageChange(language)}
            />
            {language}
          </label>
        ))}
      </div>

      <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          checked={showDifferentLanguage}
          disabled={disabled}
          onChange={(e) => !disabled && setShowDifferentLanguage(e.target.checked)}
          className="h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
        />
        Filed in a different language than the proceedings
        <InfoBox title="Language of the application (Art. 14(2) EPC)">
          You may file the application itself in a language other than
          the language of the proceedings, provided a translation is
          filed later. Leave this unchecked to file directly in the
          language of the proceedings above.
        </InfoBox>
      </label>

      {showDifferentLanguage && (
        <div className="mt-3 max-w-xs">
          <label className="mb-1 block text-sm font-medium text-gray-700">
            Language of the application *
          </label>
          <select
            value={applicationLanguage}
            disabled={disabled}
            onChange={(e) => !disabled && onApplicationLanguageChange(e.target.value)}
            className={`w-full rounded-md border border-gray-300 px-3 py-2 text-sm focus:outline-none focus:ring-1 focus:ring-orange-400 ${
              disabled ? "bg-gray-100 cursor-not-allowed" : ""
            }`}
          >
            <option value="">- Select -</option>
            {euLanguages.map((language) => (
              <option key={language} value={language}>
                {language}
              </option>
            ))}
          </select>
        </div>
      )}

      <label className="mt-4 flex cursor-pointer items-center gap-2 text-sm text-gray-600">
        <input
          type="checkbox"
          checked={examinationInNonOfficialLanguage}
          disabled={disabled}
          onChange={(e) =>
            !disabled && onExaminationInNonOfficialLanguageChange(e.target.checked)
          }
          className="h-4 w-4 rounded border-gray-300 text-dantzBlue focus:ring-dantzBlue"
        />
        Request examination in a permitted non-official language of the EPO
        (Article 14(4) EPC)
      </label>
    </div>
  )
}
